/**
 * The pick-target index: where each country sits on the sphere and how big it
 * is, in degrees of arc.
 *
 * WHY. Natural Earth 110m has no geometry at all for 29 pool members, and the
 * globe has always drawn those as dots and picked them by distance. That fixed
 * list is the wrong boundary. At the default view a degree of arc is about
 * 2.65 CSS pixels, so Luxembourg (0.39° radius) renders one pixel across,
 * Monaco renders as a dot, and Slovenia, Montenegro, Qatar, Lebanon, Jamaica
 * and sixty-odd others are a two-to-five-pixel smudge. A polygon hit test
 * cannot separate them from their neighbours, and neither can a thumb.
 *
 * So the boundary is not "has a polygon" but "is bigger than the finger". A
 * country whose on-screen radius is under `SNAP.subResolutionPx` is picked as a
 * POINT — nearest-anchor with a bias window — exactly like Vatican always was.
 * Zoom in and it crosses the threshold and goes back to being a polygon, with
 * no state and no hysteresis: the predicate is a pure function of zoom.
 *
 * The set is deliberately NOT filtered by tier pool. The globe does not know
 * which countries a match can ask about, and it must not: a target list that
 * changed with the round would leak (invariant 5).
 */

import { allCountries, featureFor } from '@geogame/data';

import type { LngLatTuple } from './geo';
import { angularSpreadDeg, lngLatOf, representativePoint, sampleVertices } from './geo';
import { globePxPerDeg } from './projection';

export interface PickTarget {
  readonly cca3: string;
  /** A point guaranteed to be inside the country, biased toward its middle. */
  readonly anchor: LngLatTuple;
  /**
   * Angular radius covering ~90% of the polygon's vertices, in degrees.
   * Zero for the countries 110m has no geometry for.
   */
  readonly angularRadiusDeg: number;
  readonly hasGeometry: boolean;
}

let targetList: readonly PickTarget[] | null = null;
let targetIndex: ReadonlyMap<string, PickTarget> | null = null;

function build(): readonly PickTarget[] {
  const out: PickTarget[] = [];
  for (const c of allCountries()) {
    if (c.isDisputed) continue;
    const base = lngLatOf(c);
    const feature = featureFor(c.cca3);
    if (!feature) {
      out.push({ cca3: c.cca3, anchor: base, angularRadiusDeg: 0, hasGeometry: false });
      continue;
    }
    const vertices = sampleVertices(feature.geometry);
    const anchor = representativePoint(feature.geometry, base) ?? base;
    out.push({
      cca3: c.cca3,
      anchor,
      angularRadiusDeg: angularSpreadDeg(anchor, vertices, 0.9),
      hasGeometry: true,
    });
  }
  return out;
}

/** Every non-disputed country, with its anchor and angular size. Built once. */
export function pickTargets(): readonly PickTarget[] {
  targetList ??= build();
  return targetList;
}

export function pickTarget(cca3: string): PickTarget | undefined {
  if (!targetIndex) {
    targetIndex = new Map(pickTargets().map((t) => [t.cca3, t] as const));
  }
  return targetIndex.get(cca3.toUpperCase());
}

/**
 * Angular radius, in degrees, below which a country is too small to aim at.
 *
 * `subResolutionPx` is an on-screen RADIUS, so the cutoff is a country whose
 * rendered footprint is under `2 * subResolutionPx` across. Halves with every
 * zoom level, which is what makes the predicate self-terminating: zoom in far
 * enough and nothing is sub-resolution any more.
 */
export function subResolutionThresholdDeg(zoom: number, subResolutionPx: number): number {
  return subResolutionPx / globePxPerDeg(zoom);
}

/** Is this country picked as a point at this zoom? Geometry-less ones always are. */
export function isSubResolution(
  target: PickTarget,
  zoom: number,
  subResolutionPx: number,
): boolean {
  return target.angularRadiusDeg < subResolutionThresholdDeg(zoom, subResolutionPx);
}
